import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search } from "lucide-react";
import { drugDbApi, medicinesApi } from "../../../shared/api/medicines";
import type { DrugDbItem, MedicineResponse } from "../../../shared/types/medicine";
import { apiError } from "../../../shared/api/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (m: MedicineResponse) => void;
}

/** Search donthuocquocgia.vn and import a record into the local catalog. */
export function DrugDbSearchModal({ open, onClose, onImported }: Props) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState<DrugDbItem | null>(null);
  const [internalCode, setInternalCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebounced("");
      setSelected(null);
      setInternalCode("");
      setError(null);
    }
  }, [open]);

  const results = useQuery({
    queryKey: ["drug-db", "search", debounced],
    queryFn: () => drugDbApi.search(debounced, 30),
    enabled: open && debounced.length >= 2,
  });

  const importIt = useMutation({
    mutationFn: () =>
      medicinesApi.importFromDrugDb({
        nationalCode: selected!.nationalCode,
        internalCode,
      }),
    onSuccess: (m) => {
      onImported(m);
      onClose();
    },
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import thuốc từ Cổng Dược Quốc Gia</DialogTitle>
          <DialogDescription>
            Tìm theo tên thuốc, hoạt chất, hoặc tên nhà sản xuất — chọn một dòng để import
            vào danh mục của phòng khám. Bạn vẫn có thể chỉnh giá bán, nhóm và mô tả sau khi import.
          </DialogDescription>
        </DialogHeader>

        {!selected && (
          <div className="space-y-4">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                className="pl-9"
                placeholder="VD: paracetamol, amoxicillin, vitamin B6…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
              />
            </div>

            <div>
              {debounced.length < 2 && (
                <p className="text-sm text-muted-foreground">
                  Nhập ít nhất 2 ký tự để bắt đầu tìm.
                </p>
              )}
              {results.isLoading && <p className="text-sm">Đang tìm…</p>}
              {results.isError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {apiError(results.error).detail}
                </div>
              )}
              {results.data?.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Không có kết quả khớp với "{debounced}".
                </p>
              )}
              {results.data && results.data.length > 0 && (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mã DQG</TableHead>
                        <TableHead>Tên thuốc</TableHead>
                        <TableHead>Hoạt chất</TableHead>
                        <TableHead>Hàm lượng</TableHead>
                        <TableHead>Đơn vị</TableHead>
                        <TableHead>Hãng</TableHead>
                        <TableHead>Barcode</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.data.map((item) => (
                        <TableRow key={item.nationalCode}>
                          <TableCell className="font-mono">{item.nationalCode}</TableCell>
                          <TableCell className="font-semibold">{item.name}</TableCell>
                          <TableCell>{item.activeIngredient ?? "—"}</TableCell>
                          <TableCell>{item.strength ?? "—"}</TableCell>
                          <TableCell>{item.unit ?? "—"}</TableCell>
                          <TableCell>{item.manufacturer ?? "—"}</TableCell>
                          <TableCell className="font-mono">{item.barcode ?? "—"}</TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => setSelected(item)}
                            >
                              Chọn
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}

        {selected && (
          <div className="space-y-4">
            <div className="rounded-md bg-muted p-4">
              <div className="mb-1 text-xs text-muted-foreground">
                Đã chọn từ Cổng Dược Quốc Gia
              </div>
              <div className="text-base font-semibold">{selected.name}</div>
              <div className="text-sm text-muted-foreground">
                Mã DQG: <span className="font-mono">{selected.nationalCode}</span>
                {selected.activeIngredient && ` · Hoạt chất: ${selected.activeIngredient}`}
                {selected.strength && ` · Hàm lượng: ${selected.strength}`}
                {selected.manufacturer && ` · ${selected.manufacturer}`}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ic">Mã thuốc nội bộ (phòng khám gán) *</Label>
              <Input
                id="ic"
                type="text"
                value={internalCode}
                onChange={(e) => setInternalCode(e.target.value)}
                placeholder="VD: LN-PRE-109"
                maxLength={64}
                autoFocus
              />
              <span className="text-xs text-muted-foreground">
                Mã hiển thị trên hoá đơn, đơn thuốc và phiếu xuất kho. Có thể bao gồm chữ và số.
              </span>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setSelected(null)}
              >
                <ArrowLeft className="h-4 w-4" />
                Chọn lại
              </Button>
              <Button
                type="button"
                disabled={!internalCode.trim() || importIt.isPending}
                onClick={() => {
                  if (!internalCode.trim()) {
                    setError("Mã thuốc nội bộ không được để trống");
                    return;
                  }
                  setError(null);
                  importIt.mutate();
                }}
              >
                {importIt.isPending ? "Đang import…" : "Import vào danh mục"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
